import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Breadcrumbs, BreadcrumbsProps, Link, Stack, TypographyProps } from '@mui/material';
import { styled } from '@mui/material/styles';

type Breadcrumb = {
  id: string;
  label: string;
  url?: string;
};

type BreadcrumbsTsoPqProps = BreadcrumbsProps & {
  breadcrumbs: Breadcrumb[] | undefined;
  bold?: boolean;
  disableLinks?: boolean;
};

type StyledLinkProps = TypographyProps & {
  active: boolean;
  bold: boolean;
};

const StyledTypography = styled(Link, {
  shouldForwardProp: (prop) => !['active', 'bold'].includes(prop as string),
})<StyledLinkProps>(({ active, bold, href }) => {
  return {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: tsoPqTheme.spacingValues.atomSpacing.spacing_atom_gap_com_gap_m,
    fontFamily: tsoPqTheme.typography.openSans,
    fontSize: tsoPqTheme.typography.fontsize_body_body_medium,
    fontWeight: bold && active ? 600 : 400,
    lineHeight: '1.25rem',
    color: active
      ? tsoPqTheme.palette.customTextColor.color_primary_font_ft_prim_accent
      : tsoPqTheme.palette.customTextColor.color_neutral_font_ft_nrtrl_enabled,
    textDecoration: 'none',

    ...(href && {
      '&:hover': {
        textDecoration: !active && 'underline',
        color: !active && tsoPqTheme.palette.customTextColor.color_neutral_font_ft_nrtrl_hover,
      },

      '&:active': {
        textDecoration: !active && 'underline',
        color: !active && tsoPqTheme.palette.customTextColor.color_neutral_font_ft_nrtrl_pressed,
      },
    }),
  };
});

const StyledNavigateNextIcon = styled(FontAwesomeIcon)(() => {
  return {
    fontSize: '0.875rem',
  };
});

const StyledBreadcrumbs = styled(Breadcrumbs)(() => {
  return {
    '& .MuiBreadcrumbs-ol': {
      display: 'flex',
      gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_8,
    },

    '& .MuiBreadcrumbs-separator': {
      margin: 0,
    },
  };
});

const BreadcrumbsTsoPq = ({ breadcrumbs, bold = false, disableLinks = false, ...props }: BreadcrumbsTsoPqProps) => {
  return (
    <Stack>
      <StyledBreadcrumbs
        {...props}
        separator={<StyledNavigateNextIcon icon={faChevronRight} />}
        aria-label="breadcrumb"
      >
        {breadcrumbs?.map((breadcrumb: Breadcrumb) => (
          <StyledTypography key={breadcrumb.id} href={disableLinks ? undefined : breadcrumb.url} active={!breadcrumb.url} bold={bold}>
            {breadcrumb.label}
          </StyledTypography>
        ))}
      </StyledBreadcrumbs>
    </Stack>
  );
};

export default BreadcrumbsTsoPq;
